<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { snakeCase } from "lodash-es";
import { useResponseError } from "~/composables/error";
import PermissionPicker from "./PermissionPicker.vue";

type AdminRoleData = {
  id: string;
  name?: string;
  key?: string;
  permissions?: string[];
};

const props = defineProps<{
  open: boolean;
  mode: "create" | "update";
  initialRole?: AdminRoleData | null;
}>();

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "saved"): void;
}>();

const { $api } = useNuxtApp();
const { t } = useI18n();
const toast = useToast();
const { formErrors, errorMsg, setErrors, clearErrors } = useResponseError();

const defaultForm = () => ({
  id: "",
  name: "",
  key: "",
  permissions: [] as string[],
});

const form = reactive(defaultForm());
const saveLoading = ref(false);
const keyTouched = ref(false);

watch(
  () => form.name,
  (val) => {
    if (!keyTouched.value) {
      form.key = snakeCase(val);
    }
  },
);

const isUpdate = computed(() => props.mode === "update");
const title = computed(() =>
  isUpdate.value ? t("adminRole.updateTitle") : t("adminRole.newTitle"),
);

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    Object.assign(form, defaultForm());
    clearErrors();
    saveLoading.value = false;
    keyTouched.value = false;

    // Update mode: parent pre-loaded the role record and passes it in via
    // :initial-role, so we hydrate the form synchronously — no spinner.
    if (isUpdate.value && props.initialRole) {
      const data = props.initialRole;
      form.id = data.id;
      form.name = data.name || "";
      form.key = data.key || "";
      form.permissions = Array.isArray(data.permissions) ? [...data.permissions] : [];
      keyTouched.value = true;
    }
  },
);

function close() {
  emit("update:open", false);
}

function onKeyInput(value: string | number) {
  keyTouched.value = true;
  form.key = String(value);
}

async function submit() {
  saveLoading.value = true;
  clearErrors();

  try {
    const payload: Record<string, any> = {
      name: form.name,
      key: form.key,
      permissions: form.permissions,
    };
    if (isUpdate.value) {
      payload.id = form.id;
      await $api("/roles", { method: "PUT", body: payload });
      toast.success({ title: t("message.updateSuccess") });
    } else {
      await $api("/roles", { method: "POST", body: payload });
      toast.success({ title: t("message.createSuccess") });
    }
    emit("saved");
    close();
  } catch (e: any) {
    setErrors(e);
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  } finally {
    saveLoading.value = false;
  }
}
</script>

<template>
  <Modal
    :open="open"
    :title="title"
    size="lg"
    @update:open="(v: boolean) => emit('update:open', v)"
    @close="close"
  >
    <div class="admin-role-form" data-testid="admin-role-form">
      <InlineMessage
        v-if="errorMsg && Object.keys(formErrors).length > 0"
        intent="negative"
        class="admin-role-form__top-error"
      >
        {{ errorMsg || t("adminRole.formError") }}
      </InlineMessage>

      <div class="admin-role-form__row">
        <FormField
          v-model="form.name"
          :label="t('adminRole.nameLabel')"
          :error="formErrors.name"
        />
        <FormField
          :model-value="form.key"
          :label="t('adminRole.keyLabel')"
          :error="formErrors.key"
          @update:model-value="onKeyInput"
        />
      </div>

      <div class="admin-role-form__permissions">
        <div class="admin-role-form__permissions-label text-body-primary">
          {{ t("adminRole.permissionsLabel") }}
        </div>
        <PermissionPicker v-model="form.permissions" />
      </div>
    </div>

    <template #actions>
      <Button variant="tertiary" size="small" @click="close">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="small"
        :disabled="saveLoading"
        data-testid="admin-role-form-save"
        @click="submit"
      >
        <ProgressCircle v-if="saveLoading" size="small" />
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.admin-role-form {
  display: flex;
  flex-direction: column;
  gap: token("gap-medium");

  &__top-error {
    margin-bottom: token("gap-2xsmall");
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: token("gap-medium");

    @include down("md") {
      grid-template-columns: 1fr;
    }
  }

  &__permissions {
    display: flex;
    flex-direction: column;
    gap: token("gap-2xsmall");
  }

  &__permissions-label {
    font-weight: 600;
  }
}
</style>
